import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Send, MessageCircle, Trash2, RefreshCw, Bot, User, Package, Filter, Workflow, Eye, Code, Bug, Info, FileText, Brain, Zap } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { confirmDialog } from '../utils/confirm';
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialLight } from "react-syntax-highlighter/dist/esm/styles/prism";

// ThinkingDots component for AI loading animation
const ThinkingDots = ({ text = "Thinking" }) => (
  <div className="thinking-container">
    <span className="thinking-text">{text}</span>
    <div className="thinking-dots">
      <div className="thinking-dot"></div>
      <div className="thinking-dot"></div>
      <div className="thinking-dot"></div>
      <div className="thinking-dot"></div>
    </div>
  </div>
);

const Chat = () => {
  const { getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [showSessions, setShowSessions] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');

  const [showProductFilter, setShowProductFilter] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productCurrentPage, setProductCurrentPage] = useState(1);
  const [productsPerPage] = useState(10);
  const [expandedSources, setExpandedSources] = useState([]);
  const [showWorkflow, setShowWorkflow] = useState(false);
  const [workflowData, setWorkflowData] = useState(null);
  const [selectedWorkflowNode, setSelectedWorkflowNode] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [messagesPerPage] = useState(10);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStreamingSession, setCurrentStreamingSession] = useState(null);
  
  // Advanced AI Assist mode state
  const [advancedAIMode, setAdvancedAIMode] = useState(false);
  
  // Assistant selection state
  const [availableAssistants, setAvailableAssistants] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState('');
  const [defaultAssistant, setDefaultAssistant] = useState('');

  useEffect(() => {
    fetchSessions();
    fetchProducts();
    fetchAIConfiguration();
    fetchAssistants(); // Fetch available assistants
    
    // Initialize Mermaid
    if (window.mermaid) {
      window.mermaid.initialize({ 
        startOnLoad: true,
        theme: 'default',
        flowchart: { useMaxWidth: true, htmlLabels: true }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentSession) {
      fetchMessages(currentSession.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Re-render Mermaid diagram when workflow data changes
    if (workflowData && window.mermaid) {
      setTimeout(() => {
        window.mermaid.init(undefined, '.mermaid');
      }, 100);
    }
  }, [workflowData]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSessions = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return; // Exit if not authenticated
      
      const response = await fetch(`${API_BASE_URL}/api/v1/chat/sessions`, {
        headers: headers
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const response = await fetch(`${API_BASE_URL}/api/v1/products/`, {
        headers: headers
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Fetch available assistants from vector store configuration
  const fetchAssistants = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const response = await fetch(`${API_BASE_URL}/api/v1/config/vector-stores`, {
        headers: headers
      });
      if (response.ok) {
        const data = await response.json();
        const assistants = [];
        
        // Extract assistants from vector stores
        if (data.vector_stores) {
          data.vector_stores.forEach((store, index) => {
            if (store.assistant_id && store.assistant_name) {
              assistants.push({
                id: store.assistant_id,
                name: store.assistant_name,
                instructions: store.assistant_instructions,
                vector_store_name: store.name,
                store_index: index
              });
            }
          });
        }
        
        setAvailableAssistants(assistants);
        
        // Set default assistant based on default vector store
        if (data.default_vector_store_index !== undefined && data.vector_stores) {
          const defaultStore = data.vector_stores[data.default_vector_store_index];
          if (defaultStore && defaultStore.assistant_id) {
            setDefaultAssistant(defaultStore.assistant_id);
            setSelectedAssistant(defaultStore.assistant_id);
          }
        } else if (assistants.length > 0) {
          // If no default is set, use the first available assistant
          setDefaultAssistant(assistants[0].id);
          setSelectedAssistant(assistants[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching assistants:', error);
    }
  };



  const generateWorkflowData = (query, productCode, sources) => {
    // Get AI engine configuration from session storage or use default
    const aiConfigStr = sessionStorage.getItem('aiConfig');
    console.log('🔍 Raw AI config from session storage:', aiConfigStr);
    
    let aiConfig = {};
    try {
      aiConfig = JSON.parse(aiConfigStr || '{}');
    } catch (error) {
      console.error('❌ Error parsing AI config from session storage:', error);
      aiConfig = {};
    }
    
    console.log('🤖 Parsed AI configuration:', aiConfig);
    
    const preferredEngine = aiConfig.preferred_engine || 'auto';
    const openaiModel = aiConfig.openai_model || 'gpt-4';
    const claudeModel = aiConfig.claude_model || 'claude-3-sonnet-20240229';
    
    console.log('🎯 AI Engine Selection:', {
      preferred: preferredEngine,
      openai: openaiModel,
      claude: claudeModel
    });
    
    console.log('🔍 Raw preferred_engine value from parsed config:', aiConfig.preferred_engine);
    console.log('🔍 Type of preferred_engine from parsed config:', typeof aiConfig.preferred_engine);
    
    const workflow = {
      query: query,
      productCode: productCode,
      timestamp: new Date().toISOString(),
      aiEngine: {
        preferred: preferredEngine,
        openai: { model: openaiModel, status: 'configured' },
        claude: { model: claudeModel, status: 'configured' },
        embedding: { model: 'sentence-transformers', status: 'active' }
      },
      nodes: [
        {
          id: 'user-input',
          name: 'User Input',
          type: 'input',
          description: 'User enters natural language question',
          data: { query: query, productCode: productCode }
        },
        {
          id: 'query-embedding',
          name: 'Query Embedding',
          type: 'process',
          description: 'Convert user query to vector embedding using sentence-transformers',
          endpoint: 'Vector Embedding Generation',
          request: { 
            query: query,
            model: 'sentence-transformers',
            dimensions: '1536'
          },
          response: { 
            status: 'Generated', 
            model: 'sentence-transformers',
            embedding_type: 'vector_array'
          }
        },
        {
          id: 'vector-search',
          name: 'Vector Search',
          type: 'process',
          description: 'Search relevant document chunks using PostgreSQL + pgvector',
          endpoint: 'PostgreSQL Vector Search',
          request: { 
            query_embedding: 'vector_array[1536]',
            account_id: 'current_user',
            product_code: productCode || 'all_products',
            limit: productCode ? 20 : 15,
            similarity_function: 'cosine_similarity'
          },
          response: { 
            chunks_found: sources?.length || 0,
            database: 'PostgreSQL with pgvector extension'
          }
        },
        {
          id: 'rag-generation',
          name: 'RAG Generation',
          type: 'process',
          description: 'Generate AI response using retrieved context and preferred AI engine',
          endpoint: 'AI Response Generation',
          request: { 
            rag_prompt: 'Enhanced prompt with inline citations',
            context_chunks: sources?.length || 0,
            preferred_engine: preferredEngine,
            ai_engines: {
              openai: { model: openaiModel, status: 'available' },
              claude: { model: claudeModel, status: 'available' }
            }
          },
          response: { 
            status: 'Generated',
            engine_used: preferredEngine === 'auto' ? 'auto-selected' : preferredEngine,
            model_used: preferredEngine === 'openai' ? openaiModel : 
                       preferredEngine === 'claude' ? claudeModel : 'auto-selected',
            answer_length: 'variable',
            citations: 'inline format'
          }
        },
        {
          id: 'response-formatting',
          name: 'Response Formatting',
          type: 'process',
          description: 'Format response with sources, citations, and clickable links',
          endpoint: 'Response Processing',
          request: { 
            ai_response: 'raw_ai_output',
            sources: sources || [],
            citation_format: 'inline [See filename.pdf, p.X]'
          },
          response: { 
            formatted_answer: 'with_citations',
            source_cards: sources?.length || 0,
            clickable_links: 'enabled',
            navigation: 'document highlighting'
          }
        },
        {
          id: 'chat-storage',
          name: 'Chat Storage',
          type: 'process',
          description: 'Save conversation to PostgreSQL database with metadata',
          endpoint: 'Database Storage',
          request: { 
            session_id: 'current_session',
            //title: first 100 characters of query,
            user_message: query,
            ai_response: 'formatted_response',
            metadata: { sources: sources?.length || 0, product_code: productCode }
          },
          response: { 
            status: 'Saved',
            message_id: 'generated_id',
            storage: 'PostgreSQL with RLS'
          }
        },
        {
          id: 'user-response',
          name: 'User Response',
          type: 'output',
          description: 'Display formatted response with inline citations and source cards',
          data: { 
            answer: 'formatted_answer',
            sources: sources || [],
            total_sources: sources?.length || 0,
            ai_engine: preferredEngine,
            model_used: preferredEngine === 'openai' ? openaiModel : 
                       preferredEngine === 'claude' ? claudeModel : 'auto-selected'
          }
        }
      ]
    };
    
    setWorkflowData(workflow);
    return workflow;
  };

  const fetchAIConfiguration = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) {
        console.log('⚠️ No auth headers available for AI configuration fetch');
        return;
      }
      
      // Test basic connectivity first
      console.log('🔍 Testing basic connectivity...');
      try {
        const healthResponse = await fetch(`${API_BASE_URL}/health`);
        console.log('✅ Basic health endpoint accessible:', healthResponse.status);
      } catch (healthError) {
        console.error('❌ Basic health endpoint not accessible:', healthError);
      }
      
      console.log('🔍 Fetching AI configuration...');
      console.log('📋 Auth headers:', headers);
      console.log('🔑 API Key being used:', headers['X-API-Key']);
      console.log('🌐 API URL being called:', `${API_BASE_URL}/api/v1/config/ai/preferred`);
      
      // Fetch preferred AI engine
      const preferredResponse = await fetch(`${API_BASE_URL}/api/v1/config/ai/preferred`, {
        headers: headers
      });
      
      console.log('📡 Preferred AI engine response status:', preferredResponse.status);
      console.log('📡 Preferred AI engine response headers:', Object.fromEntries(preferredResponse.headers.entries()));
      
      if (preferredResponse.ok) {
        const preferredData = await preferredResponse.json();
        console.log('✅ Preferred AI engine data:', preferredData);
        console.log('🔍 Raw preferred_engine value:', preferredData.preferred_engine);
        console.log('🔍 Type of preferred_engine:', typeof preferredData.preferred_engine);
        
        // Fetch AI engines configuration
        const enginesResponse = await fetch(`${API_BASE_URL}/api/v1/config/ai/engines`, {
          headers: headers
        });
        
        console.log('📡 AI engines response status:', enginesResponse.status);
        
        if (enginesResponse.ok) {
          const enginesData = await enginesResponse.json();
          console.log('✅ AI engines data:', enginesData);
          
          // Store AI configuration in session storage for workflow generation
          const aiConfig = {
            preferred_engine: preferredData.preferred_engine || 'auto',
            openai_model: enginesData.openai?.model || 'gpt-4',
            claude_model: enginesData.anthropic?.model || 'claude-3-sonnet-20240229'
          };
          
          sessionStorage.setItem('aiConfig', JSON.stringify(aiConfig));
          console.log('🤖 AI Configuration loaded and stored:', aiConfig);
          console.log('💾 Session storage value:', sessionStorage.getItem('aiConfig'));
        } else {
          console.warn('⚠️ AI engines endpoint returned status:', enginesResponse.status);
          // Try to use just the preferred engine data
          const aiConfig = {
            preferred_engine: preferredData.preferred_engine || 'auto',
            openai_model: 'gpt-4',
            claude_model: 'claude-3-sonnet-20240229'
          };
          sessionStorage.setItem('aiConfig', JSON.stringify(aiConfig));
          console.log('🤖 AI Configuration loaded (partial):', aiConfig);
        }
      } else {
        console.warn('⚠️ Preferred AI engine endpoint returned status:', preferredResponse.status);
        // Use default configuration
        const defaultConfig = {
          preferred_engine: 'auto',
          openai_model: 'gpt-4',
          claude_model: 'claude-3-sonnet-20240229'
        };
        sessionStorage.setItem('aiConfig', JSON.stringify(defaultConfig));
        console.log('🤖 Using default AI configuration:', defaultConfig);
      }
    } catch (error) {
      console.error('❌ Error fetching AI configuration:', error);
      console.error('❌ Error name:', error.name);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);
      
      // Use default configuration
      const defaultConfig = {
        preferred_engine: 'auto',
        openai_model: 'gpt-4',
        claude_model: 'claude-3-sonnet-20240229'
      };
      sessionStorage.setItem('aiConfig', JSON.stringify(defaultConfig));
      console.log('🤖 Fallback to default AI configuration:', defaultConfig);
    }
  };

  const fetchMessages = async (sessionId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return; // Exit if not authenticated
      
      const response = await fetch(`${API_BASE_URL}/api/v1/chat/sessions/${sessionId}/messages`, {
        headers: headers
      });
      if (response.ok) {
        const data = await response.json();
        // Map backend field names to frontend field names
        const mappedMessages = (data.messages || []).map(msg => ({
          ...msg,
          timestamp: msg.created_at || msg.timestamp // Use created_at from backend or fallback to timestamp
        }));
        setMessages(mappedMessages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleViewDocument = (documentId, page = null) => {
    // Navigate to documents page and highlight the specific document
    // Store the document ID and page in sessionStorage for the documents page to use
    if (page) {
      sessionStorage.setItem('highlightDocument', JSON.stringify({ id: documentId, page }));
    } else {
      sessionStorage.setItem('highlightDocument', JSON.stringify({ id: documentId }));
    }
    
    // Navigate to the documents page
    window.location.href = '/documents';
  };

  const toggleFullContent = (index) => {
    setExpandedSources(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const formatMessageContent = (content, showDebugMode = false) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    const formattedLines = [];
    
    lines.forEach((line, lineIndex) => {
      // Skip reasoning lines unless in debug mode
      if (!showDebugMode && (
        line.includes('🚀 Sending request to OpenAI') ||
        line.includes('✅ **OPENAI RESPONSE RECEIVED:**') ||
        line.includes('✅ Response Type:') ||
        line.includes('✅ Response Object:') ||
        line.includes('✅ First Choice:') ||
        line.includes('✅ Message Content:') ||
        line.includes('✅ OpenAI response received:') ||
        line.includes('✅ AI generation successful:') ||
        line.includes('🎯 AI Engine used:') ||
        line.includes('🎯 AI Response preview:') ||
        line.includes('⚠️ Warning:') ||
        line.includes('INFO:') ||
        line.includes('🔍 Validating') ||
        line.includes('❌ Claude connection test failed:') ||
        line.includes('✅ Claude validation successful:') ||
        line.includes('✅ OpenAI validation successful:') ||
        line.includes('webpack compiled') ||
        line.includes('Compiled with warnings') ||
        line.includes('[eslint]') ||
        line.includes('Choice(finish_reason=') ||
        line.includes('ChatCompletionMessage') ||
        line.includes('role=') ||
        line.includes('annotations=') ||
        line.includes('audio=') ||
        line.includes('function_call=') ||
        line.includes('tool_calls=') ||
        line.includes('refusal=') ||
        line.includes('index=') ||
        line.includes('logprobs=') ||
        line.includes('Started server process') ||
        line.includes('Finished server process') ||
        line.includes('Shutting down') ||
        line.includes('Application shutdown complete') ||
        line.includes('Started reloader process') ||
        line.includes('Waiting for application startup') ||
        line.includes('Application startup complete') ||
        line.includes('WatchFiles detected changes') ||
        line.includes('AI packages available') ||
        line.includes('Vector database') ||
        line.includes('Sentence transformer model') ||
        line.includes('zsh: killed') ||
        line.includes('uvicorn') ||
        line.includes('Uvicorn running') ||
        line.includes('Press CTRL+C to quit') ||
        line.includes('Will watch for changes') ||
        line.includes('Error loading ASGI app') ||
        line.includes('Could not import module')
      )) {
        return;
      }
      
      // Handle inline citations: [See filename.pdf, p.5] - Make them subtle and clickable
      if (line.includes('[See ') && line.includes(']')) {
        formattedLines.push(
          <div key={lineIndex} className="mb-2">
            <div className="text-dark">
              {line.split(/\[See |\]/).map((part, partIndex) => {
                if (partIndex % 2 === 1) { // Citation part
                  const citation = part.trim();
                  const match = citation.match(/(.+)\.pdf, p\.(\d+)/);
                  if (match) {
                    const filename = match[1] + '.pdf';
                    const page = match[2];
                    return (
                      <span key={partIndex} className="citation-link" onClick={() => handleCitationClick(filename, page)}>
                        [See {filename}, p.{page}]
                      </span>
                    );
                  }
                  // Handle other citation formats
                  const filenameMatch = citation.match(/(.+)\.(pdf|md)/);
                  if (filenameMatch) {
                    const filename = citation;
                    return (
                      <span key={partIndex} className="citation-link" onClick={() => handleCitationClick(filename)}>
                        [See {filename}]
                      </span>
                    );
                  }
                  return <span key={partIndex} className="citation-link">[{citation}]</span>;
                } else if (part.trim()) { // Regular text part
                  return <span key={partIndex}>{part}</span>;
                }
                return null;
              })}
            </div>
          </div>
        );
        return;
      }
      
      // Handle separators: ---
      if (line.includes('---')) {
        formattedLines.push(<hr key={lineIndex} className="my-3" />);
        return;
      }
      
      // Handle regular text (AI-generated response) with enhanced formatting
      if (line.trim()) {
        // Check if it's a heading
        if (line.startsWith('## ')) {
          formattedLines.push(
            <h3 key={lineIndex} className="text-dark fw-bold mb-3">
              {line.substring(3)}
            </h3>
          );
        } else if (line.startsWith('### ')) {
          formattedLines.push(
            <h4 key={lineIndex} className="text-secondary fw-semibold mb-2">
              {line.substring(4)}
            </h4>
          );
        } else if (line.startsWith('#### ')) {
          formattedLines.push(
            <h5 key={lineIndex} className="text-dark fw-semibold mb-2">
              {line.substring(5)}
            </h5>
          );
        } else if (line.startsWith('⚠️ WARNING:')) {
          formattedLines.push(
            <div key={lineIndex} className="alert alert-warning d-flex align-items-start gap-2 mb-3">
              <span className="fw-bold">⚠️</span>
              <span>{line.substring('⚠️ WARNING:'.length)}</span>
            </div>
          );
        } else if (line.startsWith('⚠️')) {
          formattedLines.push(
            <div key={lineIndex} className="alert alert-warning d-flex align-items-start gap-2 mb-3">
              <span className="fw-bold">⚠️</span>
              <span>{line.substring('⚠️'.length)}</span>
            </div>
          );
        } else if (line.startsWith('- ')) {
          // Handle bullet points
          formattedLines.push(
            <div key={lineIndex} className="mb-2 d-flex align-items-start gap-2">
              <span className="text-dark mt-1">•</span>
              <span className="text-dark">{line.substring(2)}</span>
            </div>
          );
        } else if (line.startsWith('1. ')) {
          // Handle numbered lists
          formattedLines.push(
            <div key={lineIndex} className="mb-2 d-flex align-items-start gap-2">
              <span className="text-dark fw-bold mt-1">{line.match(/^\d+\./)[0]}</span>
              <span className="text-dark">{line.substring(line.indexOf('.') + 2)}</span>
            </div>
          );
        } else {
          // Regular paragraph
          formattedLines.push(
            <div key={lineIndex} className="mb-2">
              <span className="text-dark">{line}</span>
            </div>
          );
        }
      }
    });
    
    return formattedLines;
  };

  // Function to handle citation clicks
  const handleCitationClick = (filename, page = null) => {
    // Find the source document in the current message
    const currentMessage = messages.find(msg => msg.role === 'assistant' && msg.sources);
    if (currentMessage && currentMessage.sources) {
      const source = currentMessage.sources.find(s => s.filename === filename);
      if (source) {
        // Open document in new tab
        const documentUrl = `${API_BASE_URL}/api/v1/documents/${source.document_id}`;
        window.open(documentUrl, '_blank');
      }
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return new Date(timestamp).toLocaleTimeString();
    } catch (error) {
      return '';
    }
  };

  const createNewSession = async () => {
    try {
      console.log('🔧 Creating new chat session...');
      
      const newSessionId = `session-${Date.now()}`;
      const newSession = {
        session_id: newSessionId,
        title: 'New Chat'
      };
      
      console.log('📤 Session data to send:', newSession);
      
      // Create session on the backend
      const authHeaders = getAuthHeaders();
      console.log('🔐 Auth headers:', authHeaders);
      
      if (!authHeaders || Object.keys(authHeaders).length === 0) {
        console.error('❌ No authentication headers available');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/v1/chat/sessions`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSession)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Response received:', data);
        
        const createdSession = {
          id: data.session_id, // Use the session_id from backend
          title: data.title,
          created_at: data.created_at
        };
        
        console.log('🎯 Created session object:', createdSession);
        
        setCurrentSession(createdSession);
        setMessages([]);
        setShowSessions(false);
        
        // Refresh sessions list
        fetchSessions();
      } else {
        console.error('❌ Failed to create session:', response.statusText);
        const errorText = await response.text();
        console.error('❌ Error details:', errorText);
      }
    } catch (error) {
      console.error('💥 Error creating session:', error);
      console.error('💥 Error stack:', error.stack);
    }
  };

  // Pagination functions
  const getCurrentMessages = () => {
    const startIndex = (currentPage - 1) * messagesPerPage;
    const endIndex = startIndex + messagesPerPage;
    const currentMessages = messages.slice(startIndex, endIndex);
    console.log(`Pagination Debug: Page ${currentPage}, Messages ${startIndex + 1}-${endIndex} of ${messages.length}, Showing ${currentMessages.length} messages`);
    return currentMessages;
  };

  const totalPages = Math.ceil(messages.length / messagesPerPage);
  console.log(`Pagination Debug: Total messages: ${messages.length}, Messages per page: ${messagesPerPage}, Total pages: ${totalPages}`);

  const patchMessageById = useCallback((messageId, patch) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) => (msg.id === messageId ? { ...msg, ...patch } : msg))
    );
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle Advanced AI Assist query with streaming
  const handleAdvancedAIAssist = async (query, productCode, messageId) => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders || Object.keys(authHeaders).length === 0) {
      throw new Error('No authentication headers available');
    }

    const formData = new FormData();
    formData.append('product_code', productCode);
    formData.append('user_query', query);
    
    // Include selected assistant if available
    if (selectedAssistant) {
      formData.append('assistant_id', selectedAssistant);
    }
    
    // Include session_id if available
    if (currentSession?.session_id) {
      formData.append('session_id', currentSession.session_id);
    }

    // Don't set Content-Type header when using FormData - let browser set it automatically
    const headersForFormData = { ...authHeaders };
    delete headersForFormData['Content-Type'];

    const response = await fetch(`${API_BASE_URL}/api/v1/advanced-ai-assist/stream`, {
      method: 'POST',
      headers: headersForFormData,
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Advanced AI assist failed: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulatedContent = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.error) {
                throw new Error(data.error);
              }
              
              if (data.status) {
                // Update message with status
                patchMessageById(messageId, {
                  content: data.message || `Status: ${data.status}`,
                  thinking: data.status !== 'completed',
                  isStreaming: data.status !== 'completed',
                });
              }
              
              if (data.chunk && data.type === 'content') {
                accumulatedContent += data.chunk;
                // Update message with accumulated content
                patchMessageById(messageId, {
                  content: accumulatedContent,
                  thinking: false,
                  isStreaming: true,
                });
              }
              
              if (data.status === 'completed') {
                // Final update with all completion data
                patchMessageById(messageId, {
                  content: accumulatedContent || 'Advanced AI assist completed',
                  thinking: false,
                  isStreaming: false,
                  metadata: {
                    product_code: data.product_code,
                    product_name: data.product_name,
                    vector_store_id: data.vector_store_id,
                    assistant_id: data.assistant_id,
                    documents_searched: data.documents_searched,
                    document_details: data.document_details,
                  },
                });
                break;
              }
            } catch (parseError) {
              console.warn('Could not parse SSE data:', line);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    return { content: accumulatedContent };
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading || !selectedProduct) return;

    const userMessage = {
      id: `msg-${Date.now()}`,
      content: inputMessage,
      role: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    // Reset to first page when new message is sent
    setCurrentPage(1);

    try {
      // Handle Advanced AI Assist mode
      if (advancedAIMode) {
        const aiMessageId = `msg-${Date.now()}-ai`;
        const aiMessage = {
          id: aiMessageId,
          content: '',
          role: 'assistant',
          timestamp: new Date().toISOString(),
          sources: [],
          isAdvanced: true
        };
        setMessages(prev => [...prev, aiMessage]);

        try {
          // Set initial thinking state
          setMessages(prev => 
            prev.map(msg => 
              msg.id === aiMessageId 
                ? { ...msg, content: 'Initializing advanced AI assist...', thinking: true, isStreaming: true, isAdvanced: true }
                : msg
            )
          );
          
          await handleAdvancedAIAssist(userMessage.content, selectedProduct, aiMessageId);
          
          // The streaming function handles all message updates
          // No additional processing needed
          
        } catch (error) {
          console.error('Advanced AI assist error:', error);
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: `❌ **Advanced AI Assist Error**: ${error.message}`, thinking: false }
              : msg
          ));
        }

        setLoading(false);
        return;
      }
      // Ensure we have a session before sending the message
      let sessionToUse = currentSession?.id;
      if (!sessionToUse) {
        // Create a new session if none exists
        const newSessionId = `session-${Date.now()}`;
        const newSession = {
          session_id: newSessionId,
          title: `Chat - ${new Date().toLocaleTimeString()}`
        };
        
        const authHeaders = getAuthHeaders();
        if (!authHeaders || Object.keys(authHeaders).length === 0) {
          console.error('No authentication headers available');
          return;
        }
        
        const sessionResponse = await fetch(`${API_BASE_URL}/api/v1/chat/sessions`, {
          method: 'POST',
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newSession)
        });
        
        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          const createdSession = {
            id: sessionData.session_id,
            title: sessionData.title,
            created_at: sessionData.created_at
          };
          setCurrentSession(createdSession);
          sessionToUse = createdSession.id;
          fetchSessions(); // Refresh sessions list
        } else {
          console.error('Failed to create session:', sessionResponse.statusText);
        }
      }
      
      // Create a placeholder AI message for streaming
      const aiMessageId = `msg-${Date.now()}-ai`;
      const aiMessage = {
        id: aiMessageId,
        content: '',
        role: 'assistant',
        timestamp: new Date().toISOString(),
        sources: [],
        isStreaming: true
      };
      setMessages(prev => [...prev, aiMessage]);
      
      // Set streaming states
      setIsGenerating(true);
      setCurrentStreamingSession(sessionToUse);

      console.log(messages); // TEST
      // Get AI configuration for the request
      const aiConfig = (() => {
        try {
          const stored = sessionStorage.getItem('aiConfig') || '{}';
          console.log('💾 Raw session storage value:', stored);
          const parsed = JSON.parse(stored);
          console.log('🔧 Parsed AI config:', parsed);
          return parsed;
        } catch (error) {
          console.warn('⚠️ Error parsing AI config, using default:', error);
          return { preferred_engine: 'auto', openai_model: 'gpt-4' };
        }
      })();
      
      const requestBody = {
        query: inputMessage,
        product_code: selectedProduct || undefined,
        session_id: sessionToUse,
        preferred_engine: (() => {
          // Force OpenAI if Claude is selected since Claude has configuration issues
          const engine = aiConfig.preferred_engine || 'openai';
          if (engine === 'claude') {
            console.log('⚠️ Claude selected but has configuration issues, falling back to OpenAI');
            return 'openai';
          }
          return engine;
        })()
      };
      
      console.log('🤖 AI Configuration for request:', aiConfig);
      console.log('📤 Request body being sent:', requestBody);
      console.log('🎯 Using preferred engine:', requestBody.preferred_engine);
      
      // Always use the regular endpoint - documents are automatically retrieved from database
      console.log('📝 Using regular endpoint with automatic document retrieval');
      
      const authHeaders = getAuthHeaders();
      if (!authHeaders || Object.keys(authHeaders).length === 0) {
        console.error('No authentication headers available');
        return;
      }

// NEW Fetch AI Response
      const response = await fetch(`${API_BASE_URL}/api/v2/chat/query`, {
        method: 'POST',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        console.log(lines); // TEST
        for (const line of lines) {
   const trimmed = line.trim();
   if (!trimmed) continue;
   if (trimmed === "--- Stream completed ---") {
       break;  // exit for-loop
    }
   if (trimmed === "data: [STOPPED]") {
       console.log("🛑 AI generation stopped by user");
       setIsGenerating(false);
       setCurrentStreamingSession(null);
       break;  // exit for-loop
    }
  //         if (line.startsWith('data: ')) {

  //           const data = line.slice(6);
  //  fullText = fullText + data;
  //           setMessages(prev =>
  //                 prev.map(msg =>
  //                   msg.id === aiMessageId
  //                     ? {
  //                         ...msg,
  //                         content: fullText,
  //                         timestamp: new Date().toISOString(),
  //                         isStreaming: false,
  //                         isError: false
  //                       }
  //                     : msg
  //                 )
  //               );

  //         }

        let data = "";
        if (line.startsWith('data: ')) {
        data = line.slice(6).replaceAll("{newline}", "\n");
        } else {
        data = line;
        }


        fullText = fullText + data;
        patchMessageById(aiMessageId, {
          content: fullText,
          timestamp: new Date().toISOString(),
          isStreaming: false,
          isError: false,
        });

        }
      }
  // END NEW Fetch AI Response

      // await fetchAIResponse(aiMessageId, inputMessage, selectedProduct, requestBody);
      // Use streaming endpoint for real-time responses
      // const response = await fetch(`${API_BASE_URL}/api/v2/chat/query`, {
      //   method: 'POST',
      //   headers: {
      //     ...authHeaders,
      //     'Content-Type': 'application/json'
      //   },
      //   body: JSON.stringify(requestBody)
      // });

      // if (response.ok) {
      //   const data = await response.json();
      //   console.log('✅ AI response received:', data);
        
      //   // Update the AI message with the response and a new timestamp
      //   setMessages(prev => prev.map(msg => 
      //     msg.id === aiMessageId 
      //       ? { 
      //           ...msg, 
      //           content: data.response || 'No response received',
      //           sources: data.sources || [],
      //           timestamp: new Date().toISOString(), // Set new timestamp when response is received
      //           isStreaming: false,
      //           isError: false
      //         }
      //       : msg
      //   ));
        
        // Update loading state
        setLoading(false);
        
        // Generate workflow data for visualization
        // generateWorkflowData(inputMessage, selectedProduct, data.sources || []);
      // } else {
        // console.error('❌ AI query failed:', response.statusText);
        // const errorText = await response.text();
        // console.error('❌ Error details:', errorText);
        
        // // Show error in the message
        // setMessages(prev => prev.map(msg => 
        //   msg.id === aiMessageId 
        //     ? { 
        //         ...msg, 
        //         content: `Error: ${response.statusText}`,
        //         timestamp: new Date().toISOString(), // Set new timestamp for error response
        //         isStreaming: false,
        //         isError: true
        //       }
        //     : msg
        // ));
        
        setLoading(false);
      // }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: `msg-${Date.now()}-error`,
        content: 'Sorry, I encountered an error. Please try again.',
        role: 'assistant',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const deleteSession = async (sessionId, databaseId) => {
    const ok = await confirmDialog({
      title: 'Delete chat session',
      content: 'Are you sure you want to delete this chat session?',
    });
    if (!ok) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/chat/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        if (currentSession?.id === databaseId) {
          setCurrentSession(null);
          setMessages([]);
        }
        fetchSessions();
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

// TEST FUNCTION



// END TEST FUNCTION

  const clearProductFilter = () => {
    setShowProductFilter(false);
    setProductSearchTerm('');
    setProductCurrentPage(1);
    // Note: No longer clearing selectedProduct as it's required
  };

  // Filter products based on search term
  const filteredProducts = products.filter(product => 
    product.product_code.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    product.name.toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  // Calculate pagination
  const totalProducts = filteredProducts.length;
  const totalProductPages = Math.ceil(totalProducts / productsPerPage);
  const productStartIndex = (productCurrentPage - 1) * productsPerPage;
  const productEndIndex = productStartIndex + productsPerPage;
  const currentProducts = filteredProducts.slice(productStartIndex, productEndIndex);

  const handleProductSearch = (searchTerm) => {
    setProductSearchTerm(searchTerm);
    setProductCurrentPage(1); // Reset to first page when searching
  };

  const handleProductPageChange = (page) => {
    setProductCurrentPage(page);
  };

  const handleProductSelect = (productCode) => {
    setSelectedProduct(productCode);
    setShowProductFilter(false); // Close the filter after selection
    
    // Focus the input field after a short delay to ensure the filter panel has closed
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const handleStopGeneration = async () => {
    if (!currentStreamingSession) return;

    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/chat/sessions/${currentStreamingSession}/stop`, {
        method: 'POST',
        headers: authHeaders
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ AI generation stop requested:', data.message);
        
        // Update UI immediately
        setIsGenerating(false);
        setCurrentStreamingSession(null);
        
        // Update the streaming message to show it was stopped
        const stopMessage = data.reason === 'active_stopped' 
          ? "*[Response cancelled by user]*"
          : "*[Generation already completed]*";
          
        setMessages(prev => prev.map(msg => 
          msg.isStreaming ? {
            ...msg,
            content: msg.content + `\n\n${stopMessage}`,
            isStreaming: false,
            timestamp: new Date().toISOString()
          } : msg
        ));
        
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to stop AI generation:', errorData.detail || response.statusText);
      }
    } catch (error) {
      console.error('❌ Error stopping AI generation:', error);
    }
  };

  return (
    <div className="container-fluid h-100">
      <div className="row h-100">
        {/* Sessions Sidebar */}
        <div className={`col-md-3 ${showSessions ? 'd-block' : 'd-none d-md-block'}`}>
          <div className="card h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Chat Sessions</h6>
              <button
                className="btn btn-sm btn-dark"
                onClick={createNewSession}
              >
                <MessageCircle size={16} />
              </button>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                {sessions.map((session) => (
                  <button
                    key={session.id}
                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                      currentSession?.id === session.id ? 'active' : ''
                    }`}
                    onClick={() => setCurrentSession(session)}
                  >
                    <div className="text-truncate">
                      <div className="fw-bold">{session.title}</div>
                      <small className="text-muted">
                        {new Date(session.created_at).toLocaleString()}
                      </small>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSession(session.session_id, session.id);
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </button>
                ))}
                {sessions.length === 0 && (
                  <div className="text-center py-4 text-muted">
                    <MessageCircle size={32} className="mb-2" />
                    <p>No chat sessions yet</p>
                    <button
                      className="btn btn-sm btn-dark"
                      onClick={createNewSession}
                    >
                      Start New Chat
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="col-md-9">
          <div className="card h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-0">
                  {currentSession ? currentSession.title : 'AI Chat'}
                </h6>
                {currentSession && (
                  <small className="text-muted">
                    Created {new Date(currentSession.created_at).toLocaleString()}
                  </small>
                )}
                {showDebug && (
                  <div className="mt-1">
                    <span className="badge bg-light text-dark border border-dark">
                      <Bug size={12} className="me-1" />
                      Debug Mode Active
                    </span>
                    <small className="d-block text-muted mt-1">
                      Showing AI reasoning and technical details
                    </small>
                  </div>
                )}
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary d-md-none"
                  onClick={() => setShowSessions(!showSessions)}
                >
                  <MessageCircle size={16} />
                </button>
                {currentSession && (
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={createNewSession}
                  >
                    <RefreshCw size={16} />
                  </button>
                )}
                <button
                  className="btn btn-sm btn-outline-info"
                  onClick={() => setShowWorkflow(!showWorkflow)}
                  title="Show RAG Workflow"
                >
                  <Workflow size={16} />
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowDebug(!showDebug)}
                  title="Toggle Debug Information"
                >
                  {showDebug ? <Bug size={16} /> : <Info size={16} />}
                </button>
              </div>
            </div>
            
            <div className="card-body d-flex flex-column p-0">
              {/* Product Filter */}
              {currentSession && (
                <div className="border-bottom p-3">
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className={`btn btn-sm ${selectedProduct ? 'btn-dark' : 'btn-outline-secondary'}`}
                      onClick={() => setShowProductFilter(!showProductFilter)}
                    >
                      <Filter size={16} className="me-1" />
                      {selectedProduct 
                        ? `Selected: ${selectedProduct}` 
                        : `Select Product Required (${products.length} available)`
                      }
                    </button>
                    {selectedProduct && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={clearProductFilter}
                      >
                        Close
                      </button>
                    )}
                  </div>
                  
                  {showProductFilter && (
                    <div className="mt-3">
                      <div className="mb-3">
                        <label className="form-label fw-semibold mb-2">Select Product (Required for Chat)</label>
                        
                        {/* Search Input */}
                        <div className="mb-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search products..."
                            value={productSearchTerm}
                            onChange={(e) => handleProductSearch(e.target.value)}
                          />
                        </div>

                        {/* Product Selection Required Notice */}
                        {!selectedProduct && (
                          <div className="mb-3">
                            <div className="alert alert-warning border-0">
                              <div className="d-flex align-items-center gap-2">
                                <Package size={16} className="text-warning" />
                                <div>
                                  <div className="fw-semibold">Product Selection Required</div>
                                  <div className="small">Please select a specific product to start chatting</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Products List */}
                        {totalProducts > 0 ? (
                          <div>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <small className="text-muted fw-semibold">
                                Products ({totalProducts} {productSearchTerm ? 'found' : 'total'})
                              </small>
                              {productSearchTerm && (
                                <button 
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => handleProductSearch('')}
                                >
                                  Clear Search
                                </button>
                              )}
                            </div>
                            
                            {/* Product Cards */}
                            {currentProducts.map((product) => (
                              <div key={product.product_code} className="mb-2">
                                <div 
                                  className={`card p-3 cursor-pointer ${selectedProduct === product.product_code ? 'bg-dark text-white' : 'bg-white border'}`}
                                  onClick={() => handleProductSelect(product.product_code)}
                                  style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <div className={`rounded-circle p-1 ${selectedProduct === product.product_code ? 'bg-white text-dark' : 'bg-secondary text-white'}`}>
                                      <Package size={12} />
                                    </div>
                                    <div className="flex-grow-1">
                                      <div className="fw-semibold">{product.product_code}</div>
                                      <div className="small text-muted">{product.name}</div>
                                    </div>
                                    {selectedProduct === product.product_code && (
                                      <div className="badge bg-white text-dark">Selected</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}

                            {/* Pagination */}
                            {totalProductPages > 1 && (
                              <div className="d-flex justify-content-center mt-3">
                                <nav aria-label="Product pagination">
                                  <ul className="pagination pagination-sm">
                                    <li className={`page-item ${productCurrentPage === 1 ? 'disabled' : ''}`}>
                                      <button 
                                        className="page-link"
                                        onClick={() => handleProductPageChange(productCurrentPage - 1)}
                                        disabled={productCurrentPage === 1}
                                      >
                                        Previous
                                      </button>
                                    </li>
                                    
                                    {[...Array(totalProductPages)].map((_, index) => {
                                      const page = index + 1;
                                      // Show first, last, current, and adjacent pages
                                      if (
                                        page === 1 ||
                                        page === totalProductPages ||
                                        (page >= productCurrentPage - 1 && page <= productCurrentPage + 1)
                                      ) {
                                        return (
                                          <li key={page} className={`page-item ${productCurrentPage === page ? 'active' : ''}`}>
                                            <button 
                                              className="page-link"
                                              onClick={() => handleProductPageChange(page)}
                                            >
                                              {page}
                                            </button>
                                          </li>
                                        );
                                      } else if (
                                        page === productCurrentPage - 2 ||
                                        page === productCurrentPage + 2
                                      ) {
                                        return (
                                          <li key={page} className="page-item disabled">
                                            <span className="page-link">...</span>
                                          </li>
                                        );
                                      }
                                      return null;
                                    })}
                                    
                                    <li className={`page-item ${productCurrentPage === totalProductPages ? 'disabled' : ''}`}>
                                      <button 
                                        className="page-link"
                                        onClick={() => handleProductPageChange(productCurrentPage + 1)}
                                        disabled={productCurrentPage === totalProductPages}
                                      >
                                        Next
                                      </button>
                                    </li>
                                  </ul>
                                </nav>
                              </div>
                            )}

                            {/* Pagination Info */}
                            <div className="text-center mt-2">
                              <small className="text-muted">
                                Showing {productStartIndex + 1}-{Math.min(productEndIndex, totalProducts)} of {totalProducts} products
                              </small>
                            </div>
                          </div>
                        ) : (
                          <div>
                            {productSearchTerm ? (
                              <div className="text-center py-3">
                                <Package size={32} className="text-muted mb-2" />
                                <div className="text-muted">
                                  No products found matching "{productSearchTerm}"
                                </div>
                                <button 
                                  className="btn btn-sm btn-outline-secondary mt-2"
                                  onClick={() => handleProductSearch('')}
                                >
                                  Clear Search
                                </button>
                              </div>
                            ) : (
                              <div className="alert alert-light border-0 bg-light">
                                <div className="d-flex align-items-center gap-2">
                                  <Package size={16} className="text-dark" />
                                  <div className="flex-grow-1">
                                    <div className="small fw-semibold">No Products Available</div>
                                    <div className="small text-muted">
                                      Create some products first to enable document filtering
                                    </div>
                                  </div>
                                  <Link to="/products" className="btn btn-sm btn-outline-dark">
                                    <Package size={14} className="me-1" />
                                    Create Product
                                  </Link>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  

                  
                  {/* RAG Workflow Panel */}
                  {showWorkflow && (
                    <div className="border-bottom p-3 bg-light">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <Workflow size={20} className="text-dark" />
                          <h6 className="mb-0 text-dark">RAG Workflow Visualization</h6>
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => {
                              fetchAIConfiguration();
                              if (workflowData) {
                                // Regenerate workflow with updated AI config
                                const updatedWorkflow = generateWorkflowData(
                                  workflowData.query, 
                                  workflowData.productCode, 
                                  workflowData.nodes[2]?.response?.chunks_found ? 
                                    Array(workflowData.nodes[2].response.chunks_found).fill({}) : []
                                );
                                console.log('🔄 Workflow regenerated with updated AI config:', updatedWorkflow);
                              }
                            }}
                            title="Refresh AI Configuration"
                          >
                            <RefreshCw size={14} className="me-1" />
                            Refresh AI Config
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setShowWorkflow(false)}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      
                      {workflowData ? (
                        <div className="row">
                          <div className="col-md-8">
                            <div className="bg-white p-3 rounded border">
                              <div className="mermaid">
                                {`graph TD
    A[User Input<br/>${workflowData.query.substring(0, 30)}${workflowData.query.length > 30 ? '...' : ''}] --> B[Query Embedding<br/>sentence-transformers<br/>1536 dimensions]
    B --> C[Vector Search<br/>PostgreSQL + pgvector<br/>cosine_similarity]
    C --> D[RAG Generation<br/>${workflowData.aiEngine.preferred === 'auto' ? 'Auto-select' : workflowData.aiEngine.preferred}<br/>${workflowData.aiEngine.preferred === 'openai' ? workflowData.aiEngine.openai.model : workflowData.aiEngine.preferred === 'claude' ? workflowData.aiEngine.claude.model : 'Auto-selected'}]
    D --> E[Response Formatting<br/>Citations + Sources<br/>Clickable Links]
    E --> F[Chat Storage<br/>PostgreSQL + RLS<br/>Metadata]
    F --> G[User Response<br/>Formatted Answer<br/>AI: ${workflowData.aiEngine.preferred}]
    
    style A fill:#e1f5fe,stroke:#01579b,stroke-width:2px
    style G fill:#e8f5e8,stroke:#2e7d32,stroke-width:2px
    style B fill:#fff3e0,stroke:#f57c00,stroke-width:2px
    style C fill:#fff3e0,stroke:#f57c00,stroke-width:2px
    style D fill:#fff3e0,stroke:#f57c00,stroke-width:2px
    style E fill:#fff3e0,stroke:#f57c00,stroke-width:2px
    style F fill:#fff3e0,stroke:#f57c00,stroke-width:2px
    
    classDef inputNode fill:#e1f5fe,stroke:#01579b,stroke-width:2px
    classDef outputNode fill:#e8f5e8,stroke:#2e7d32,stroke-width:2px
    classDef processNode fill:#fff3e0,stroke:#f57c00,stroke-width:2px
    
    class A inputNode
    class G outputNode
    class B,C,D,E,F processNode`}
                              </div>
                              
                              {/* AI Engine Information */}
                              <div className="mt-3 p-3 bg-light rounded border border-dark">
                                <h6 className="mb-2 text-dark">
                                  <Code size={16} className="me-2" />
                                  AI Engine Configuration
                                </h6>
                                <div className="row g-2">
                                  <div className="col-md-3">
                                    <div className="text-center p-2 bg-white rounded">
                                      <div className="h6 mb-0 text-dark">
                                        {workflowData.aiEngine.preferred === 'auto' ? '🔄 Auto' : 
                                         workflowData.aiEngine.preferred === 'openai' ? '🤖 OpenAI' : 
                                         workflowData.aiEngine.preferred === 'claude' ? '🧠 Claude' : 'Auto'}
                                      </div>
                                      <small className="text-muted">Preferred Engine</small>
                                    </div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="text-center p-2 bg-white rounded">
                                      <div className="h6 mb-0 text-dark">
                                        {workflowData.aiEngine.openai.model}
                                      </div>
                                      <small className="text-muted">OpenAI Model</small>
                                    </div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="text-center p-2 bg-white rounded">
                                      <div className="h6 mb-0 text-dark">
                                        {workflowData.aiEngine.claude.model}
                                      </div>
                                      <small className="text-muted">Claude Model</small>
                                    </div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="text-center p-2 bg-white rounded">
                                      <div className="h6 mb-0 text-dark">
                                        {workflowData.aiEngine.embedding.model}
                                      </div>
                                      <small className="text-muted">Embedding Model</small>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Debug Information */}
                              {showDebug && (
                                <div className="mt-3 p-3 bg-light rounded border border-dark">
                                  <h6 className="mb-2 text-dark">
                                    <Code size={16} className="me-2" />
                                    Debug Information
                                  </h6>
                                  <div className="row g-2">
                                    <div className="col-md-6">
                                      <div className="small">
                                        <strong>Session Storage AI Config:</strong>
                                        <pre className="mt-1 mb-0 bg-white p-2 rounded small">
                                          {JSON.stringify(JSON.parse(sessionStorage.getItem('aiConfig') || '{}'), null, 2)}
                                        </pre>
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="small">
                                        <strong>Current Workflow AI Config:</strong>
                                        <pre className="mt-1 mb-0 bg-white p-2 rounded small">
                                          {JSON.stringify(workflowData.aiEngine, null, 2)}
                                        </pre>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Clickable Node Information */}
                              <div className="mt-3">
                                <h6 className="mb-2">Click on workflow nodes to see details:</h6>
                                <div className="row g-2">
                                  {workflowData.nodes.map((node, index) => (
                                    <div key={node.id} className="col-md-4">
                                      <button
                                        className={`btn btn-sm w-100 ${
                                          node.type === 'input' ? 'btn-outline-primary' :
                                          node.type === 'output' ? 'btn-outline-success' :
                                          'btn-outline-secondary'
                                        }`}
                                        onClick={() => setSelectedWorkflowNode(node)}
                                        title={`Click to see ${node.name} details`}
                                      >
                                        <Eye size={14} className="me-1" />
                                        {node.name}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="bg-white p-3 rounded border">
                              <h6 className="mb-2">Workflow Details</h6>
                              <div className="small">
                                <div className="mb-2">
                                  <strong>Query:</strong> {workflowData.query}
                                </div>
                                <div className="mb-2">
                                  <strong>Product:</strong> {workflowData.productCode || 'All Products'}
                                </div>
                                <div className="mb-2">
                                  <strong>Sources Found:</strong> {workflowData.nodes[2].response.chunks_found}
                                </div>

                                <div className="mb-2">
                                  <strong>Timestamp:</strong> {new Date(workflowData.timestamp).toLocaleTimeString()}
                                </div>
                                
                                <hr className="my-2" />
                                
                                <h6 className="mb-2 text-dark">AI Engine Details</h6>
                                <div className="mb-2">
                                  <strong>Preferred Engine:</strong> 
                                  <span className="badge ms-1 bg-dark text-white">
                                    {workflowData.aiEngine.preferred === 'auto' ? '🔄 Auto' : 
                                     workflowData.aiEngine.preferred === 'openai' ? '🤖 OpenAI' : 
                                     workflowData.aiEngine.preferred === 'claude' ? '🧠 Claude' : 'Auto'}
                                  </span>
                                </div>
                                <div className="mb-2">
                                  <strong>OpenAI Model:</strong> 
                                  <span className="badge bg-light text-dark ms-1">
                                    {workflowData.aiEngine.openai.model}
                                  </span>
                                </div>
                                <div className="mb-2">
                                  <strong>Claude Model:</strong> 
                                  <span className="badge bg-light text-dark ms-1">
                                    {workflowData.aiEngine.claude.model}
                                  </span>
                                </div>
                                <div className="mb-2">
                                  <strong>Embedding Model:</strong> 
                                  <span className="badge bg-light text-dark ms-1">
                                    {workflowData.aiEngine.embedding.model}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-3 text-muted">
                          <Workflow size={32} className="mb-2" />
                          <p>Send a message to see the RAG workflow visualization</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* API Call Details Modal */}
                  {selectedWorkflowNode && (
                    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                      <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title">
                              <Code size={16} className="me-2" />
                              {selectedWorkflowNode.name} - API Details
                            </h5>
                            <button
                              type="button"
                              className="btn-close"
                              onClick={() => setSelectedWorkflowNode(null)}
                            ></button>
                          </div>
                          <div className="modal-body">
                            <div className="row">
                              <div className="col-md-6">
                                <h6>Request Details</h6>
                                <div className="bg-light p-3 rounded">
                                  <pre className="small mb-0">
                                    {JSON.stringify(selectedWorkflowNode.request || selectedWorkflowNode.data, null, 2)}
                                  </pre>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <h6>Response Details</h6>
                                <div className="bg-light p-3 rounded">
                                  <pre className="small mb-0">
                                    {JSON.stringify(selectedWorkflowNode.response || selectedWorkflowNode.data, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                            {selectedWorkflowNode.endpoint && (
                              <div className="mt-3">
                                <h6>Endpoint Information</h6>
                                <div className="bg-light p-3 rounded">
                                  <div className="small">
                                    <strong>Endpoint:</strong> {selectedWorkflowNode.endpoint}
                                  </div>
                                  <div className="small">
                                    <strong>Description:</strong> {selectedWorkflowNode.description}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="modal-footer">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setSelectedWorkflowNode(null)}
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Messages */}
              <div className="flex-grow-1 p-3" style={{ height: '400px', overflowY: 'auto' }}>
                {!currentSession ? (
                  <div className="text-center py-5">
                    <Bot size={64} className="text-muted mb-3" />
                    <h5>Welcome to AI Chat</h5>
                    <p className="text-muted">Start a new chat session to begin asking questions</p>
                    <button
                      className="btn btn-dark"
                      onClick={createNewSession}
                    >
                      <MessageCircle size={20} className="me-2" />
                      Start New Chat
                    </button>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-5">
                    {selectedProduct ? (
                      <>
                        <MessageCircle size={64} className="text-muted mb-3" />
                        <h5>New Chat Session</h5>
                        <p className="text-muted">
                          Ask me anything about product: <strong>{selectedProduct}</strong>
                        </p>
                      </>
                    ) : (
                      <>
                        <Package size={64} className="text-muted mb-3" />
                        <h5>Product Selection Required</h5>
                        <p className="text-muted">
                          Please select a product above to start chatting with AI about its documents.
                        </p>
                        <button
                          className="btn btn-outline-secondary mt-2"
                          onClick={() => setShowProductFilter(true)}
                        >
                          <Package size={16} className="me-2" />
                          Select Product
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {getCurrentMessages().map((message) => (
                      <div
                        key={message.id}
                        className={`d-flex ${message.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                      >
                        <div
                          className={`d-flex align-items-start gap-2 ${
                            message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                          }`}
                          style={{ maxWidth: '80%' }}
                        >
                          <div className={`rounded-circle d-flex align-items-center justify-content-center ${
                            message.role === 'user' ? 'bg-dark' : 'bg-light border'
                          }`}
                          style={{ width: '32px', height: '32px', minWidth: '32px' }}
                          >
                            {message.role === 'user' ? (
                              <User size={16} className="text-white" />
                            ) : (
                              <Bot size={16} className="text-white" />
                            )}
                          </div>
                          <div
                            className={`p-3 rounded ${
                              message.role === 'user'
                                ? 'bg-primary text-white'
                                : message.isError
                                ? 'bg-danger text-white'
                                : 'bg-light'
                            }`}
                          >
                            {/* Only show message content for user messages - AI responses handled separately */}
                            {message.role === 'user' && (
                              <div className="mb-1 message-content">
                                {message.content}
                              </div>
                            )}
                            
                                                        {/* Enhanced Response Presentation - Only for AI Messages */}
                            {message.role === 'assistant' && (
                              <div className="mt-3">
                                                                  {/* Response Summary */}
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                   {/* <div className="badge bg-success-subtle text-success border border-success">
                                      <Bot size={12} className="me-1" />
                                      {message.isStreaming ? <ThinkingDots text="AI" /> : 'AI Response'}
                                   </div> */}
                                  {message.sources && message.sources.length > 0 && (
                                    <div className="badge bg-info-subtle text-info border border-info">
                                      <FileText size={12} className="me-1" />
                                      {message.sources.length} Source{message.sources.length !== 1 ? 's' : ''}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Response Display - Handle both streaming and complete messages */}
                                {message.isError ? (
                                  <div className="bg-danger-subtle p-3 rounded border border-danger response-container">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <div className="text-danger">⚠️</div>
                                      <span className="fw-semibold text-danger">Error:</span>
                                    </div>
                                    <div className="text-danger">
                                      {message.content}
                                    </div>
                                  </div>
                                                                ) : (
                                  <>
                                    {/* Clean Response Display (Non-Debug Mode) */}
                                    {!showDebug && (
                                      <div className="bg-light p-3 rounded border response-container">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                          <div className="d-flex align-items-center gap-2">
                                            {message.isAdvanced ? (
                                              <Brain size={16} className="text-success" />
                                            ) : (
                                              <Bot size={16} className="text-success" />
                                            )}
                                            <span className="fw-semibold text-success">
                                              {message.isStreaming ? <ThinkingDots text="AI is thinking" /> : (message.isAdvanced ? 'Vector Search Results:' : 'Answer:')}
                                            </span>
                                            {message.isAdvanced && (
                                              <div className="badge bg-success ms-2">Advanced AI</div>
                                            )}
                                          </div>
                                          {message.isStreaming && isGenerating && (
                                            <button
                                              onClick={handleStopGeneration}
                                              className="chatgpt-stop-button"
                                              title="Stop generating"
                                            >
                                              <div className="stop-icon">
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                  <rect x="3" y="3" width="8" height="8" rx="1" fill="currentColor"/>
                                                </svg>
                                              </div>
                                            </button>
                                          )}
                                        </div>
                                        {message.content ? (
                                          <div className="clean-response">
                                          <ReactMarkdown
                                            components={{
                                              code({ node, inline, className, children, ...props }) {
                                                const match = /language-(\w+)/.exec(className || "");
                                                return !inline && match ? (
                                                  <SyntaxHighlighter
                                                    style={materialLight}
                                                    language={match[1]}
                                                    PreTag="div"
                                                    {...props}
                                                  >
                                                    {String(children).replace(/\n$/, "")}
                                                  </SyntaxHighlighter>
                                                ) : (
                                                  <code className={className} {...props}>
                                                    {children}
                                                  </code>
                                                );
                                              }
                                            }}
                                          >
                                            {message.content}
                                          </ReactMarkdown>
                                        </div>
                                                                                ) : (
                                          <div className="text-center py-4">
                                             <ThinkingDots />
                                          </div>
                                        )}
                                        {message.isStreaming && (
                                          <div className="mt-2 text-center">
                                             {/* <ThinkingDots text="Streaming response" /> */}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Full Response Display (Debug Mode) */}
                                    {showDebug && (
                                      <div className="bg-warning-subtle p-3 rounded border border-warning response-container">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                          <div className="d-flex align-items-center gap-2">
                                            <Bug size={16} className="text-warning" />
                                            <span className="fw-semibold text-warning">Full Response (Debug Mode):</span>
                                          </div>
                                          {message.isStreaming && isGenerating && (
                                            <button
                                              onClick={handleStopGeneration}
                                              className="chatgpt-stop-button"
                                              title="Stop generating"
                                            >
                                              <div className="stop-icon">
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                  <rect x="3" y="3" width="8" height="8" rx="1" fill="currentColor"/>
                                                </svg>
                                              </div>
                                            </button>
                                          )}
                                        </div>
                                        {message.content ? (
                                          <div className="debug-response">
                                            {formatMessageContent(message.content, true)}
                                          </div>
                                                                                ) : (
                                          <div className="text-center py-4">
                                             <ThinkingDots />
                                          </div>
                                        )}
                                        {message.isStreaming && (
                                          <div className="mt-2 text-center">
                                            {/* <ThinkingDots text="Debug streaming" /> */}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                                
                                {/* Debug Toggle for Sources */}
                                {message.sources && message.sources.length > 0 && (
                                  <div className="mb-3">
                                    <button
                                      className={`btn btn-sm debug-toggle ${
                                        debugInfo[message.id] 
                                          ? 'btn-warning' 
                                          : 'btn-outline-secondary'
                                      }`}
                                      onClick={() => {
                                        const newDebugInfo = { ...debugInfo };
                                        newDebugInfo[message.id] = !newDebugInfo[message.id];
                                        setDebugInfo(newDebugInfo);
                                      }}
                                      title={debugInfo[message.id] ? "Hide Source Information" : "Show Source Information"}
                                    >
                                      {debugInfo[message.id] ? (
                                        <>
                                          <Eye size={14} className="me-1" />
                                          Hide Sources
                                        </>
                                      ) : (
                                        <>
                                          <Bug size={14} className="me-1" />
                                          Show Sources
                                        </>
                                      )}
                                    </button>
                                  </div>
                                )}
                                
                                {/* Sources Display (Debug Section) */}
                                {message.sources && message.sources.length > 0 && debugInfo[message.id] && (
                                  <div className="mt-3 p-3 bg-light rounded border">
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <FileText size={16} className="text-info" />
                                      <h6 className="mb-0 text-info">Source Documents</h6>
                                    </div>
                                    <div className="row g-3">
                                      {message.sources.map((source, index) => (
                                        <div key={index} className="col-12">
                                          <div 
                                            className="card border bg-white source-card"
                                            onClick={() => handleViewDocument(source.document_id)}
                                            title="Click to view document"
                                          >
                                            <div className="card-body p-3">
                                              <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div className="d-flex align-items-center gap-2">
                                                  <span className="badge bg-dark">
                                                    {source.file_type?.toUpperCase() || 'DOC'}
                                                  </span>
                                                  <span className="fw-bold text-dark">
                                                    {source.filename}
                                                  </span>
                                                  {source.page_reference && (
                                                    <span className="badge bg-light text-dark">
                                                      Page {source.page_reference}
                                                    </span>
                                                  )}
                                                </div>
                                                <button
                                                  className="btn btn-sm btn-outline-primary"
                                                  title="View Document"
                                                  onClick={() => handleViewDocument(source.document_id)}
                                                >
                                                  <Eye size={14} className="me-1" />
                                                  View
                                                </button>
                                              </div>
                                              
                                              <div className="row g-2 mb-2">
                                                <div className="col-md-6">
                                                  <small className="text-muted">
                                                    <strong>Product:</strong> {source.product_code || 'Not specified'}
                                                  </small>
                                                </div>
                                                <div className="col-md-6">
                                                  <small className="text-muted">
                                                    <strong>Relevance:</strong> {source.relevance_score}
                                                  </small>
                                                </div>
                                              </div>
                                              
                                              <div className="mb-2">
                                                <small className="text-muted d-block mb-1">
                                                  <strong>Content Preview:</strong>
                                                </small>
                                                <div className="small bg-light p-2 rounded">
                                                  {source.content_preview}
                                                </div>
                                              </div>
                                              
                                              {source.full_content && (
                                                <div>
                                                  <button
                                                    className="btn btn-sm btn-outline-info"
                                                    onClick={() => toggleFullContent(index)}
                                                  >
                                                    {expandedSources.includes(index) ? (
                                                      <>
                                                        <Eye size={14} className="me-1" />
                                                        Show Less
                                                      </>
                                                    ) : (
                                                      <>
                                                        <FileText size={14} className="me-1" />
                                                        Show Full Content
                                                      </>
                                                    )}
                                                  </button>
                                                  {expandedSources.includes(index) && (
                                                    <div className="mt-2 p-3 bg-light rounded border">
                                                      <small className="text-muted d-block mb-2">
                                                        <strong>📖 Full Document Section:</strong>
                                                      </small>
                                                      <div className="small" style={{ whiteSpace: 'pre-wrap', maxHeight: '300px', overflowY: 'auto' }}>
                                                        {source.full_content}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <div className="mt-3 pt-2 border-top">
                              <div className="d-flex align-items-center justify-content-between">
                                <small className="text-muted">
                                  {message.role === 'assistant' ? (
                                    <>
                                      <Bot size={12} className="me-1" />
                                      {formatTimestamp(message.timestamp)}
                                    </>
                                  ) : (
                                    <>
                                      <User size={12} className="me-1" />
                                      {formatTimestamp(message.timestamp)}
                                    </>
                                  )}
                                </small>
                                {message.role === 'assistant' && message.sources && message.sources.length > 0 && (
                                  <small className="text-muted">
                                    📚 {message.sources.length} source{message.sources.length !== 1 ? 's' : ''} used
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Loading indicator removed - now handled by streaming messages */}
                    
                    {/* Pagination Controls */}
                    {console.log(`Pagination Controls Debug: totalPages=${totalPages}, messages.length=${messages.length}, shouldShow=${totalPages > 1}`)}
                    
                    {/* Always show pagination info */}
                    <div className="d-flex justify-content-center mt-4">
                      <div className="text-center mb-2">
                        <small className="text-muted">
                          Showing {((currentPage - 1) * messagesPerPage) + 1} to {Math.min(currentPage * messagesPerPage, messages.length)} of {messages.length} messages
                        </small>
                      </div>
                    </div>
                    
                    {/* Pagination Navigation - only show when multiple pages */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-center">
                        <nav aria-label="Chat history pagination">
                          <ul className="pagination pagination-sm">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                Previous
                              </button>
                            </li>
                            
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                              
                              return (
                                <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(pageNum)}
                                  >
                                    {pageNum}
                                  </button>
                                </li>
                              );
                            })}
                            
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Input Area */}
              {currentSession && (
                <div className="border-top p-3">
                  
                  {/* Advanced AI Mode Toggle */}
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="form-check form-switch">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="advancedAIToggle"
                        checked={advancedAIMode}
                        onChange={(e) => setAdvancedAIMode(e.target.checked)}
                      />
                      <label className="form-check-label d-flex align-items-center gap-2" htmlFor="advancedAIToggle">
                        {advancedAIMode ? <Brain size={16} className="text-success" /> : <Bot size={16} className="text-primary" />}
                        <span className="fw-semibold">
                          {advancedAIMode ? 'Advanced AI Assist' : 'Regular Chat'}
                        </span>
                        {advancedAIMode && <Zap size={14} className="text-warning" />}
                      </label>
                    </div>
                    
                    {advancedAIMode && (
                      <div className="d-flex align-items-center gap-2">
                        <div className="badge bg-success">Vector Search</div>
                        {selectedProduct && (
                          <div className="badge bg-info">{selectedProduct}</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Assistant Selection */}
                  {advancedAIMode && availableAssistants.length > 0 && (
                    <div className="mb-3">
                      <label htmlFor="assistantSelect" className="form-label fw-semibold">
                        🤖 Select Assistant
                      </label>
                      <select 
                        id="assistantSelect"
                        className="form-select"
                        value={selectedAssistant}
                        onChange={(e) => setSelectedAssistant(e.target.value)}
                      >
                        {availableAssistants.map((assistant) => (
                          <option key={assistant.id} value={assistant.id}>
                            {assistant.name} ({assistant.vector_store_name})
                            {assistant.id === defaultAssistant && ' - Default'}
                          </option>
                        ))}
                      </select>
                      {selectedAssistant && (
                        <div className="form-text">
                          {availableAssistants.find(a => a.id === selectedAssistant)?.instructions || 'No instructions provided'}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Mode Description */}
                  {advancedAIMode && (
                    <div className="alert alert-success border-0 mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <Brain size={16} className="text-success" />
                        <div className="flex-grow-1">
                          <div className="fw-semibold">Advanced AI Assist Mode</div>
                          <div className="small">
                            Searches vector stores for product-specific documents using OpenAI's advanced search capabilities.
                            {selectedAssistant && (
                              <span className="d-block mt-1">
                                <strong>Using Assistant:</strong> {availableAssistants.find(a => a.id === selectedAssistant)?.name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Product Selection Notice */}
                  {!selectedProduct && (
                    <div className="alert alert-info border-0 mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <Package size={16} className="text-info" />
                        <div className="flex-grow-1">
                          <div className="fw-semibold">Product Selection Required</div>
                          <div className="small">
                            {advancedAIMode 
                              ? "Select a specific product above to use Advanced AI Assist with vector search."
                              : "Select a specific product above to start chatting with AI about its documents."
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Input Form */}
                  <form onSubmit={handleSendMessage} className="d-flex gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      className="form-control"
                      placeholder={
                        selectedProduct 
                          ? (advancedAIMode 
                              ? `Search vector store for ${selectedProduct}...`
                              : `Ask about documents from ${selectedProduct}...`)
                          : "Please select a product first to start chatting..."
                      }
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      disabled={loading || !selectedProduct}
                    />
                    <button
                      type="submit"
                      className={`btn ${advancedAIMode ? 'btn-success' : 'btn-primary'}`}
                      disabled={!inputMessage.trim() || loading || !selectedProduct}
                      title={advancedAIMode ? 'Send Advanced AI Query' : 'Send Message'}
                    >
                      {advancedAIMode ? <Brain size={20} /> : <Send size={20} />}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;

// Add CSS for improved styling
const style = document.createElement('style');
style.textContent = `
  .workflow-node {
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .workflow-node:hover {
    transform: scale(1.05);
  }
  
  .mermaid {
    text-align: center;
  }
  
  .mermaid svg {
    max-width: 100%;
    height: auto;
  }
  
  .workflow-details {
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #dee2e6;
  }
  
  .workflow-stats {
    background: white;
    border-radius: 6px;
    border: 1px solid #dee2e6;
  }
  
  .message-content {
    line-height: 1.6;
    font-size: 0.95rem;
  }
  
  .message-content h1, .message-content h2, .message-content h3,
  .message-content h4, .message-content h5, .message-content h6 {
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    font-weight: 600;
  }
  
  .message-content p {
    margin-bottom: 1rem;
  }
  
  .message-content ul, .message-content ol {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
  }
  
  .message-content li {
    margin-bottom: 0.5rem;
  }
  
  .message-content blockquote {
    border-left: 4px solid #dee2e6;
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
    color: #6c757d;
  }
  
  .message-content code {
    background-color: #f8f9fa;
    padding: 0.2rem 0.4rem;
    border-radius: 0.25rem;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9em;
  }
  
  .message-content pre {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin: 1rem 0;
  }
  
  .message-content pre code {
    background-color: transparent;
    padding: 0;
  }
  
  .source-card {
    transition: all 0.3s ease;
    border: 1px solid #e9ecef;
    background: #ffffff;
    cursor: pointer;
  }
  
  .source-card:hover {
    border-color: #000000;
    transform: translateY(-2px);
  }
  
  .source-card:active {
    transform: translateY(0);
  }
  
  .debug-toggle {
    transition: all 0.2s ease;
  }
  
  .debug-toggle:hover {
    transform: translateY(-1px);
  }
  
  .clean-response {
    font-size: 1rem;
    line-height: 1.7;
    color: #000000;
  }
  
  .clean-response h1, .clean-response h2, .clean-response h3,
  .clean-response h4, .clean-response h5, .clean-response h6 {
    color: #000000;
    font-weight: 600;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
  }
  
  .clean-response h1 { font-size: 1.5rem; }
  .clean-response h2 { font-size: 1.4rem; }
  .clean-response h3 { font-size: 1.3rem; }
  .clean-response h4 { font-size: 1.2rem; }
  .clean-response h5 { font-size: 1.1rem; }
  .clean-response h6 { font-size: 1rem; }
  
  .clean-response p {
    margin-bottom: 1rem;
  }
  
  .clean-response ul, .clean-response ol {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
  }
  
  .clean-response li {
    margin-bottom: 0.5rem;
  }
  
  .clean-response blockquote {
    border-left: 4px solid #000000;
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
    color: #6c757d;
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 0.25rem;
  }
  
  .clean-response code {
    background-color: #e9ecef;
    padding: 0.2rem 0.4rem;
    border-radius: 0.25rem;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9em;
    color: #000000;
  }
  
  .clean-response pre {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin: 1rem 0;
    border: 1px solid #dee2e6;
  }
  
  .clean-response pre code {
    background-color: transparent;
    padding: 0;
    color: inherit;
  }
  
  .debug-response {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.85rem;
    line-height: 1.4;
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 0.5rem;
    border: 1px solid #dee2e6;
    max-height: 400px;
    overflow-y: auto;
  }
  
  .debug-response::-webkit-scrollbar {
    width: 8px;
  }
  
  .debug-response::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  .debug-response::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  .debug-response::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  
  .response-container {
    transition: all 0.3s ease;
    border: 1px solid #e9ecef;
    background: #ffffff;
  }
  
  .response-container:hover {
    transform: translateY(-2px);
    border-color: #dee2e6;
  }
  
  /* Enhanced response header */
  .response-container .d-flex.align-items-center.gap-2.mb-2 {
    padding: 0.75rem 1rem;
    margin: -0.75rem -0.75rem 1rem -0.75rem;
    background: #000000;
    color: white;
    border-radius: 0.375rem 0.375rem 0 0;
    border-bottom: 1px solid #e9ecef;
  }
  
  .response-container .d-flex.align-items-center.gap-2.mb-2 .text-success {
    color: white !important;
  }
  
  .response-container .d-flex.align-items-center.gap-2.mb-2 .fw-semibold {
    color: white !important;
    font-weight: 600 !important;
  }
  
  /* Streaming indicator styles */
  .streaming-indicator {
    animation: pulse 1.5s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  
  .spinner-border-sm {
    width: 0.75rem;
    height: 0.75rem;
  }
  
  /* Citation link styles - subtle and professional */
  .citation-link {
    color: #6c757d;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.9em;
    background-color: #f8f9fa;
    padding: 0.1rem 0.3rem;
    border-radius: 0.25rem;
    border: 1px solid #e9ecef;
  }
  
  .citation-link:hover {
    color: #495057;
    background-color: #e9ecef;
    border-color: #adb5bd;
    transform: translateY(-1px);
  }
  
  .citation-link:active {
    transform: translateY(0);
  }
  
  /* Enhanced response styling with card sections */
  .clean-response h3 {
    color: #000000;
    border-bottom: 2px solid #000000;
    padding: 1rem 1.5rem;
    margin: 1.5rem -1.5rem 1.5rem -1.5rem;
    position: relative;
    background: #ffffff;
    border-radius: 0.5rem 0.5rem 0 0;
    border: 1px solid #e9ecef;
    border-bottom: 2px solid #000000;
  }
  
  .clean-response h3::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 3px;
    background: #000000;
    border-radius: 2px;
  }
  
  .clean-response h4 {
    color: #000000;
    border-left: 3px solid #000000;
    padding: 0.75rem 1rem;
    margin: 1rem 0;
    position: relative;
    background: #f8f9fa;
    border-radius: 0.375rem;
    
    border: 1px solid #dee2e6;
  }
  
  .clean-response h4::before {
    content: '';
    position: absolute;
    left: -3px;
    top: 0;
    height: 100%;
    width: 3px;
    background: #000000;
    border-radius: 2px;
  }
  
  .clean-response h5 {
    color: #000000;
    font-weight: 600;
    position: relative;
    padding: 0.5rem 0.75rem;
    background: #ffffff;
    border-radius: 0.25rem;
    margin-bottom: 0.75rem;
    border-left: 2px solid #000000;
  }
  
  .clean-response h5::before {
    content: '▶';
    position: absolute;
    left: 0;
    top: 0;
    color: #000000;
    font-size: 0.8em;
  }
  
  .clean-response .alert {
    border-radius: 0.5rem;
    border: none;
    
  }
  
  .clean-response .alert-warning {
    background: #f8f9fa;
    color: #000000;
  }
  
  /* Warning and info boxes as special cards */
  .clean-response .text-warning {
    background: #f8f9fa;
    padding: 1rem 1.5rem;
    border-radius: 0.5rem;
    border: 1px solid #dee2e6;
    
    margin: 1rem 0;
    transition: all 0.2s ease;
  }
  
  .clean-response .text-warning:hover {
    
    transform: translateY(-1px);
  }
  
  .clean-response .text-info {
    background: #f8f9fa;
    padding: 1rem 1.5rem;
    border-radius: 0.5rem;
    border: 1px solid #dee2e6;
    
    margin: 1rem 0;
    transition: all 0.2s ease;
  }
  
  .clean-response .text-info:hover {
    transform: translateY(-1px);
  }
  
  /* Enhanced bullet points and numbered lists */
  .clean-response .d-flex.align-items-start.gap-2 {
    padding: 0.5rem;
    border-radius: 0.25rem;
    transition: background-color 0.2s ease;
  }
  
  .clean-response .d-flex.align-items-start.gap-2:hover {
    background-color: #f8f9fa;
  }
  
  .clean-response .text-primary.mt-1 {
    font-size: 1.2em;
    color: #000000 !important;
  }
  
  .clean-response .text-primary.fw-bold.mt-1 {
    color: #000000 !important;
    font-weight: 700 !important;
  }
  
  /* Enhanced paragraph spacing and typography with card appearance */
  .clean-response p {
    line-height: 1.8;
    color: #000000;
    margin-bottom: 1.2rem;
    padding: 0.75rem 1rem;
    background: #ffffff;
    border-radius: 0.375rem;
    border: 1px solid #e9ecef;
     0 1px 2px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
  }
  
  .clean-response p:hover {
    
    border-color: #dee2e6;
  }
  
  .clean-response p:last-child {
    margin-bottom: 0;
  }
  
  /* Enhanced list styling with card appearance */
  .clean-response ul, .clean-response ol {
    margin-bottom: 1.5rem;
    padding: 1rem 1.5rem;
    background: #ffffff;
    border-radius: 0.5rem;
    border: 1px solid #e9ecef;
     0 2px 4px rgba(0, 0, 0, 0.08);
    transition: all 0.2s ease;
  }
  
  .clean-response ul:hover, .clean-response ol:hover {
     0 3px 6px rgba(0, 0, 0, 0.12);
    border-color: #dee2e6;
  }
  
  .clean-response li {
    margin-bottom: 0.8rem;
    line-height: 1.6;
    color: #000000;
    padding: 0.25rem 0;
  }
  
  .clean-response li:last-child {
    margin-bottom: 0;
  }
  
  /* Enhanced blockquote styling with card appearance */
  .clean-response blockquote {
    border-left: 4px solid #000000;
    padding: 1.5rem;
    margin: 1.5rem 0;
    font-style: italic;
    color: #6c757d;
    background: #f8f9fa;
    border-radius: 0.5rem;
    position: relative;
    
    border: 1px solid #dee2e6;
    transition: all 0.2s ease;
  }
  
  .clean-response blockquote:hover {
     0 4px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
  
  .clean-response blockquote::before {
    content: '"';
    position: absolute;
    top: 0.5rem;
    left: 1rem;
    font-size: 3rem;
    color: #000000;
    font-family: serif;
    line-height: 1;
  }
  
  /* Enhanced code styling */
  .clean-response code {
    background: #f8f9fa;
    padding: 0.3rem 0.6rem;
    border-radius: 0.375rem;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9em;
    color: #000000;
    border: 1px solid #dee2e6;
  }
  
  .clean-response pre {
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 0.75rem;
    overflow-x: auto;
    margin: 1.5rem 0;
    border: 1px solid #dee2e6;
    
  }
  
  .clean-response pre code {
    background: transparent;
    padding: 0;
    color: inherit;
    border: none;
  }
  
  /* Step-by-step sections as special cards */
  .clean-response h3 + p,
  .clean-response h3 + ul,
  .clean-response h3 + ol {
    background: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    padding: 1.5rem;
    margin-top: 0;
    
    transition: all 0.2s ease;
  }
  
  .clean-response h3 + p:hover,
  .clean-response h3 + ul:hover,
  .clean-response h3 + ol:hover {
    
    border-color: #dee2e6;
  }
  
  /* Maintenance recommendations as special card */
  .clean-response h3:contains("Maintenance") + p,
  .clean-response h3:contains("Recommendations") + p {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 0.5rem;
    padding: 1.5rem;
    color: #000000;
     0 3px 8px rgba(40, 167, 69, 0.15);
    transition: all 0.2s ease;
  }
  
  .clean-response h3:contains("Maintenance") + p:hover,
  .clean-response h3:contains("Recommendations") + p:hover {
     0 4px 10px rgba(40, 167, 69, 0.2);
    transform: translateY(-1px);
  }
  
  /* Enhanced spacing between sections */
  .clean-response > * {
    margin-bottom: 1.5rem;
  }
  
  .clean-response > *:last-child {
    margin-bottom: 0;
  }
  
  /* Pagination styling */
  .pagination .page-link {
    color: #000000;
    border-color: #dee2e6;
    transition: all 0.2s ease;
  }
  
  .pagination .page-link:hover {
    background-color: #e9ecef;
    border-color: #000000;
    color: #000000;
  }
  
  .pagination .page-item.active .page-link {
    background-color: #000000;
    border-color: #000000;
    color: white;
  }
  
  .pagination .page-item.disabled .page-link {
    color: #6c757d;
    background-color: #fff;
    border-color: #dee2e6;
  }

  /* Thinking dots animation */
  .thinking-container {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

  .thinking-dots {
    display: flex;
    gap: 0.2rem;
    align-items: center;
  }

  .thinking-dot {
    width: 0.5rem;
    height: 0.5rem;
    background-color: #6c757d;
    border-radius: 50%;
    animation: thinking-bounce 1.4s infinite ease-in-out;
  }

  .thinking-dot:nth-child(1) {
    animation-delay: -0.32s;
  }

  .thinking-dot:nth-child(2) {
    animation-delay: -0.16s;
  }

  .thinking-dot:nth-child(3) {
    animation-delay: 0;
  }

  .thinking-dot:nth-child(4) {
    animation-delay: 0.16s;
  }

  @keyframes thinking-bounce {
    0%, 80%, 100% {
      transform: scale(0);
      opacity: 0.5;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }

  /* Thinking text styling */
  .thinking-text {
    color: #6c757d;
    font-style: italic;
    font-weight: 500;
  }

  /* ChatGPT-style Stop Button */
  .chatgpt-stop-button {
    background: none;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    padding: 0.375rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease-in-out;
    color: #374151;
    min-width: 32px;
    height: 32px;
  }

  .chatgpt-stop-button:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
    color: #111827;
  }

  .chatgpt-stop-button:active {
    background-color: #f3f4f6;
    border-color: #6b7280;
    transform: scale(0.98);
  }

  .chatgpt-stop-button:focus {
    outline: none;
    box-shadow: 0 0 0 2px #3b82f6, 0 0 0 4px rgba(59, 130, 246, 0.1);
  }

  .chatgpt-stop-button .stop-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .chatgpt-stop-button .stop-icon svg {
    transition: color 0.15s ease-in-out;
  }

  /* Dark theme compatibility */
  .dark .chatgpt-stop-button {
    border-color: #4b5563;
    color: #9ca3af;
  }

  .dark .chatgpt-stop-button:hover {
    background-color: #374151;
    border-color: #6b7280;
    color: #f9fafb;
  }

  .dark .chatgpt-stop-button:active {
    background-color: #4b5563;
  }
`;
document.head.appendChild(style);

